import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, KeyRound, Lock, Loader2, ArrowRight, Sun, Moon, ShieldCheck } from 'lucide-react';
import api from '../api/axios';

const customStyles = `
  @keyframes blob {
    0% { transform: translate(0px, 0px) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  .animate-blob { animation: blob 15s infinite alternate ease-in-out; }
`;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // Step 1: Request OTP, Step 2: Reset Password
  
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [isDark, setIsDark] = useState(() => {
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme) return storedTheme === 'dark';
    if (document.documentElement.classList.contains('dark')) return true;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  // Step 1: Request the OTP
  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      await api.post('/forgot-password/request', { email });
      setStep(2);
      setSuccessMessage('An OTP has been sent to your email address.');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError('Failed to request password reset. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify OTP and Reset
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    setError('');

    try {
      await api.post('/forgot-password/reset', { 
        email: email, 
        otp: otp, 
        new_password: newPassword 
      });
      
      setSuccessMessage('Password reset successfully! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired OTP');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-slate-50 dark:bg-[#05050A] text-slate-900 dark:text-slate-200 font-sans flex items-center justify-center relative overflow-hidden overflow-x-hidden transition-colors duration-500 px-4 py-4 sm:py-6">
      <style>{customStyles}</style>

      {/* Theme Toggle Button */}
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-50">
        <button 
          onClick={() => setIsDark(!isDark)} 
          className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors duration-300 focus:outline-none ${isDark ? 'bg-indigo-500/20 border border-indigo-500/30' : 'bg-slate-200 border border-slate-300 shadow-sm'}`}
        >
          <span className={`inline-flex h-4 w-4 transform items-center justify-center rounded-full transition-transform duration-300 ${isDark ? 'translate-x-7 bg-indigo-400' : 'translate-x-1 bg-white shadow-sm'}`}>
            {isDark ? <Moon size={10} className="text-[#0a0a0a]" /> : <Sun size={10} className="text-slate-400" />}
          </span>
        </button>
      </div>

      {/* Ambient Glows */}
      <div className="absolute top-[10%] left-[20%] w-[40%] h-[40%] rounded-full bg-indigo-200/40 dark:bg-indigo-600/10 blur-[120px] animate-blob z-0 pointer-events-none"></div>
      <div className="absolute bottom-[10%] right-[20%] w-[40%] h-[40%] rounded-full bg-violet-200/40 dark:bg-violet-600/10 blur-[120px] animate-blob z-0 pointer-events-none" style={{ animationDelay: '2s' }}></div>

      <div className="w-full max-w-md relative z-10 animate-in fade-in zoom-in-95 duration-500">
        
        <div className="flex flex-col items-center justify-center mb-6 sm:mb-8">
          <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-[1rem] sm:rounded-[1.2rem] flex items-center justify-center mb-4 shadow-md dark:shadow-2xl backdrop-blur-md">
            {step === 1 ? <Mail size={24} className="text-indigo-600 dark:text-indigo-400" strokeWidth={1.5} /> : <KeyRound size={24} className="text-indigo-600 dark:text-indigo-400" strokeWidth={1.5} />}
          </div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-wide text-slate-900 dark:text-white text-center transition-colors">
            {step === 1 ? 'Reset Password' : 'Secure Reset'}
          </h1>
          <p className="text-slate-500 text-[12px] font-normal tracking-[0.1em] [word-spacing:-0.05em] mt-2 text-center">
            {step === 1 ? 'Enter your email to receive a code' : 'Enter your OTP and new password'}
          </p>
        </div>

        <form onSubmit={step === 1 ? handleRequestOtp : handleResetPassword} className="bg-white/80 dark:bg-[#0a0a0a]/50 backdrop-blur-xl border border-slate-200 dark:border-white/10 p-6 sm:p-8 rounded-[2rem] shadow-xl dark:shadow-2xl flex flex-col gap-5">
          
          {successMessage && (
            <div className="text-emerald-700 dark:text-emerald-400 text-xs font-medium bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 p-3 rounded-xl flex items-center gap-2">
              <ShieldCheck size={16} /> {successMessage}
            </div>
          )}

          {step === 1 ? (
             <div className="space-y-4 animate-in slide-in-from-left-4 fade-in">
               <div>
                 <label className="block text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide mb-1.5 flex items-center gap-2">
                   <Mail size={14} className="text-indigo-500 dark:text-indigo-400"/> Account Email
                 </label>
                 <input
                   type="email" required disabled={isLoading}
                   placeholder="you@company.com" value={email} onChange={e => setEmail(e.target.value)}
                   className="w-full bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-sm placeholder:text-[12px] font-medium text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all disabled:opacity-50"
                 />
               </div>
             </div>
          ) : (
             <div className="space-y-4 animate-in slide-in-from-right-4 fade-in">
               <div>
                 <label className="block text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide mb-1.5 flex items-center gap-2">
                   <KeyRound size={14} className="text-indigo-500 dark:text-indigo-400"/> 6-Digit OTP
                 </label>
                 <input 
                   type="text" required disabled={isLoading} maxLength={6}
                   placeholder="123456" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                   className="w-full bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-center tracking-[0.5em] text-lg font-mono text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all disabled:opacity-50"
                 />
               </div>

               <div>
                 <label className="block text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide mb-1.5 flex items-center gap-2">
                   <Lock size={14} className="text-indigo-500 dark:text-indigo-400"/> New Password
                 </label>
                 <input 
                   type="password" required disabled={isLoading}
                   placeholder="••••••••" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                   className="w-full bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-sm placeholder:text-[12px] font-medium text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all disabled:opacity-50"
                 />
               </div>

               <div>
                 <label className="block text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide mb-1.5 flex items-center gap-2">
                   <Lock size={14} className="text-indigo-500 dark:text-indigo-400"/> Confirm Password
                 </label>
                 <input 
                   type="password" required disabled={isLoading}
                   placeholder="••••••••" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                   className="w-full bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-sm placeholder:text-[12px] font-medium text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all disabled:opacity-50"
                 />
               </div>
             </div>
          )}

          {error && (
            <div className="text-rose-600 dark:text-rose-500 text-xs font-medium bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/20 p-2.5 rounded-xl">
              {error}
            </div>
          )}

          <button 
            type="submit" disabled={isLoading || (step === 1 && !email) || (step === 2 && (!otp || !newPassword || !confirmPassword))}
            className="w-full bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-500 text-white rounded-xl py-3.5 text-xs font-bold tracking-wide transition-all shadow-md dark:shadow-[0_0_15px_rgba(99,102,241,0.3)] active:scale-95 flex items-center justify-center gap-3 disabled:opacity-50 mt-1"
          >
            {isLoading ? <Loader2 size={16} className="animate-spin"/> : step === 1 ? 'Send Reset Code' : 'Update Password'}
            {!isLoading && <ArrowRight size={16} strokeWidth={2}/>}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-slate-500 dark:text-slate-400 text-[12px] font-medium tracking-[0.1em] [word-spacing:-0.05em]">
            Remember your password?{' '}
            <Link to="/login" className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-white transition-colors">
              Back to Sign In
            </Link>
          </p>
        </div>

      </div>
    </div>
  );
};

export default ForgotPassword;